import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";

import {
  fetchPaymentHistory,
  checkStripeConnection,
  connectStripe,
  disconnectStripe,
} from "store/paymentSlice";
import PaymentOptionsView from "./components/PaymentOptionsView/PaymentOptionsView";
import PaymentHistory from "components/PaymentHistory/PaymentHistory";
import StripeProvider from "./components/StripeProvider/StripeProvider";

import styles from "./PaymentMethodsTab.module.scss";

const Payment: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedProvider, setSelectedProvider] = useState("stripe");

  const {
    paymentHistory,
    loading,
    isStripeConnected = false,
    stripeConnectedDate,
  } = useSelector((state: RootState) => state.payment);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await dispatch(checkStripeConnection()).unwrap();
        if (isStripeConnected) {
          await dispatch(fetchPaymentHistory()).unwrap();
        }
      } catch (error) {
        console.error("Error loading initial data", error);
      }
    };

    loadInitialData();
  }, [dispatch, isStripeConnected]);

  const handleStripeAction = () => {
    if (isStripeConnected) {
      dispatch(disconnectStripe());
    } else {
      dispatch(connectStripe());
    }
  };

  const formatConnectedDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return "";
    }
  };

  const renderProviderContent = () => {
    switch (selectedProvider) {
      case "stripe":
        return (
          <StripeProvider
            isConnected={isStripeConnected}
            connectedDate={formatConnectedDate(stripeConnectedDate)}
            onAction={handleStripeAction}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <div className={styles.title}>Payment Methods</div>
        <div className={styles.subtitle}>
          Setup your preferred payment methods
        </div>
      </div>
      <div className={styles.selectionSection}>
        <PaymentOptionsView
          isStripeConnected={isStripeConnected}
          selectedProvider={selectedProvider}
          onSelectProvider={setSelectedProvider}
        />
      </div>

      <div className={styles.selectionContent}>
        <div className={styles.providerSection}>{renderProviderContent()}</div>

        <div className={styles.historySection}>
          <PaymentHistory
            history={paymentHistory}
            isStripeConnected={isStripeConnected}
            onSetupStripe={() => dispatch(connectStripe())}
          />
        </div>
      </div>
    </div>
  );
};

export default Payment;
